import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import type { DealCompMapEntry } from "./DealCompComparisonMap";
import { DealCompMapPopUp } from "./DealCompMapPopUp";
import "../../assets/css/GoogleMapsPopUp.css";

export type DealCompDuplicateMapEntryItem = {
  entry: DealCompMapEntry;
  index: number;
};

export type DealCompDuplicateMapPopUpProps = {
  allEntries: readonly DealCompDuplicateMapEntryItem[];
  compMapPopupVariant: "lease" | "loan" | "sales";
  firstRowTrailing?: React.ReactNode;
  onSelectEntry?: (entry: DealCompMapEntry, index: number) => void;
};

function dropdownLabel(entry: DealCompMapEntry): string {
  const asset =
    entry.asset_name?.trim() ||
    entry.location?.trim() ||
    "—";
  const tenant = entry.tenant_name?.trim() || "—";
  return `${asset} · ${tenant}`;
}

function popupPropsForEntry(
  entry: DealCompMapEntry,
  compMapPopupVariant: "lease" | "loan" | "sales",
) {
  if (compMapPopupVariant === "sales") {
    return {
      variant: "sales" as const,
      buyer: entry.buyer,
      vendor: entry.vendor,
      currency: entry.currency,
      location: entry.location,
      area_type: entry.area_type,
      yield_pct: entry.yield_pct,
      sales_price: entry.sales_price,
      price_per_area: entry.price_per_area ?? null,
      no_of_keys_or_rooms: entry.no_of_keys_or_rooms,
      price_per_key_room: entry.price_per_key_room ?? null,
      age_refurb_date: entry.age_refurb_date ?? null,
    };
  }
  if (compMapPopupVariant === "lease") {
    return {
      variant: "lease" as const,
      web_url: entry.web_url ?? null,
      city: entry.city,
      source: entry.source,
      deal_name: entry.deal_name ?? null,
      currency: entry.currency,
      asset_type: entry.asset_type,
      asset_name: entry.asset_name,
      lease_term: entry.lease_term,
      total_rent: entry.total_rent,
      rent_per_area: entry.rent_per_area ?? null,
      tenant_name: entry.tenant_name,
      age_refurb_date: entry.age_refurb_date ?? null,
    };
  }
  return {
    variant: "loan" as const,
    location: entry.location,
    lender: entry.lender,
    borrower: entry.borrower,
    loan_amount: entry.loan_amount,
  };
}

/** Sentinel — avoids MUI Select quirks with `value=""`. */
const PLACEHOLDER_VALUE = "__none__";

export const DealCompDuplicateMapPopUp: React.FC<
  DealCompDuplicateMapPopUpProps
> = ({
  allEntries,
  compMapPopupVariant,
  firstRowTrailing,
  onSelectEntry,
}) => {
  const onSelectEntryRef = useRef(onSelectEntry);
  onSelectEntryRef.current = onSelectEntry;

  /** Stable across parent re-renders so we only reset when the duplicate pin’s rows actually change. */
  const duplicateGroupKey = useMemo(() => {
    if (allEntries.length === 0) return "";
    const e0 = allEntries[0].entry;
    const ids = [...allEntries.map((r) => r.index)]
      .sort((a, b) => a - b)
      .join(",");
    return `${e0.lat},${e0.lng}:${ids}`;
  }, [allEntries]);

  const [selectedListIndex, setSelectedListIndex] = useState<number | null>(
    () => (allEntries.length > 0 ? 0 : null),
  );

  /** When this duplicate group changes, default to the first comp and notify parent. */
  useEffect(() => {
    if (allEntries.length === 0) {
      setSelectedListIndex(null);
      return;
    }
    setSelectedListIndex(0);
    const first = allEntries[0];
    onSelectEntryRef.current?.(first.entry, first.index);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `allEntries` matches `duplicateGroupKey`
  }, [duplicateGroupKey]);

  const useLightTheme = true;

  const selectValue =
    selectedListIndex === null || allEntries.length === 0
      ? PLACEHOLDER_VALUE
      : String(selectedListIndex);

  const selectedItem =
    selectedListIndex !== null &&
    selectedListIndex >= 0 &&
    selectedListIndex < allEntries.length
      ? allEntries[selectedListIndex]
      : null;

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const raw = event.target.value;
    if (raw === PLACEHOLDER_VALUE) {
      setSelectedListIndex(null);
      return;
    }
    const listIdx = Number(raw);
    if (!Number.isFinite(listIdx) || listIdx < 0 || listIdx >= allEntries.length) {
      setSelectedListIndex(null);
      return;
    }
    setSelectedListIndex(listIdx);
    const row = allEntries[listIdx];
    onSelectEntry?.(row.entry, row.index);
  };

  return (
    <Box
      className="map-info-top-box"
      sx={{
        color: useLightTheme ? "#182737" : "#FFF",
        backgroundColor: useLightTheme ? "#FFF" : "#182737",
        borderRadius: "8px",
        minWidth: "200px",
        maxWidth: 320,
      }}
    >
      {/* Header row — matches MapPopUp when showCloseButton */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          columnGap: 1,
          rowGap: 0,
          pt: "6px",
          px: "12px",
          pb: 0,
        }}
      >
        <Box
          className="map-info-name-box"
          sx={{
            flex: 1,
            minWidth: 0,
            padding: "0 !important",
          }}
        >
          <Typography
            className="map-info-title"
            sx={{
              color: useLightTheme ? "#182737" : "#FFF",
            }}
          >
            Multiple comps
          </Typography>
          <Typography
            className="map-info-sub-title"
            sx={{
              color: useLightTheme ? "#6B7886" : "rgba(255, 255, 255, 0.8)",
            }}
          >
            Same map pin — pick a comp to view details.
          </Typography>
        </Box>
        {firstRowTrailing ? (
          <Box sx={{ flexShrink: 0, lineHeight: 0 }}>{firstRowTrailing}</Box>
        ) : null}
      </Box>

      <Box className="map-info-values-box-wrapper">
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel id="deal-comp-dup-select-label" shrink>
            Comp
          </InputLabel>
          <Select
            labelId="deal-comp-dup-select-label"
            id="deal-comp-dup-select"
            label="Comp"
            value={selectValue}
            onChange={handleSelectChange}
            displayEmpty
            MenuProps={{
              // Portal + fixed stacking: inside Google Maps / InfoWindow, `disablePortal`
              // keeps the list in a transformed ancestor so Popper mispositions the menu.
              disablePortal: false,
              disableScrollLock: true,
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              PaperProps: {
                sx: {
                  maxHeight: 280,
                  zIndex: 2100,
                },
              },
            }}
            renderValue={(value) => {
              if (value === PLACEHOLDER_VALUE) {
                return (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                      fontWeight: 400,
                    }}
                  >
                    Select asset & tenant…
                  </Typography>
                );
              }
              const idx = Number(value);
              const row = allEntries[idx];
              if (!row) {
                return (
                  <Typography component="span" sx={{ fontSize: 14 }}>
                    —
                  </Typography>
                );
              }
              return (
                <Typography
                  component="span"
                  className="map-info-values"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#182737",
                    lineHeight: 1.35,
                  }}
                >
                  {dropdownLabel(row.entry)}
                </Typography>
              );
            }}
            sx={{
              borderRadius: 1,
              "& .MuiSelect-select": {
                fontSize: 14,
                py: 1,
              },
            }}
          >
            <MenuItem value={PLACEHOLDER_VALUE} disabled>
              <em>Select asset & tenant…</em>
            </MenuItem>
            {allEntries.map(({ entry }, listIndex) => (
              <MenuItem key={String(listIndex)} value={String(listIndex)}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0.25,
                    py: 0.25,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#182737",
                      lineHeight: 1.35,
                    }}
                  >
                    {entry.asset_name?.trim() ||
                      entry.location?.trim() ||
                      "—"}
                  </Typography>
                  <Typography
                    component="span"
                    className="map-info-sub-title"
                    sx={{
                      fontSize: 12,
                      color: "#6B7886",
                      lineHeight: 1.2,
                    }}
                  >
                    {entry.tenant_name?.trim() || "—"}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedItem ? (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              mx: -1.5,
              px: 1.5,
              pb: 0.5,
            }}
          >
            <DealCompMapPopUp
              {...popupPropsForEntry(
                selectedItem.entry,
                compMapPopupVariant,
              )}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
